import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPlus, faCheck, faUser, faShieldAlt, faClock, faSyncAlt, faLock } from "@fortawesome/free-solid-svg-icons";
import { updateUserSessionTime } from "../../store/slices/authSlice";
import settingsAPI from "../../services/settingsService";
import Table from "../../components/Common/Table/Table";
import Heading from "../../components/Common/Heading/Heading";
import ActionButtons from "../../components/Common/ActionButtons/ActionButtons";
import SkeletonLoader from "../../components/SkeletonLoader/SkeletonLoader";
import ListScreenSearchBar from "../../components/Common/ListScreenSearchBar/ListScreenSearchBar";
import ListScreenPaginationBar from "../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import "./Settings.css";

// --- Follow Up Template Component ---
const FollowUpSetting = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: "", template: "" });
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getFollowUpTemplates();
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    let filtered = templates;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = templates.filter(
        (t) =>
          t.name?.toLowerCase().includes(searchLower) ||
          t.template?.toLowerCase().includes(searchLower)
      );
    }
    setFilteredTemplates(filtered);
    setCurrentPage(1);
  }, [templates, searchTerm]);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: "", template: "" });
    setShowModal(true);
  };

  const handleEditTemplate = async (templateId) => {
    try {
      const response = await settingsAPI.getTemplateById(templateId);
      if (response.success) {
        setEditingTemplate(response.data);
        setFormData({
          name: response.data.name || "",
          template: response.data.template || "",
        });
        setShowModal(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }
    try {
      const response = await settingsAPI.deleteTemplate(templateId);
      if (response.success) {
        toast.success(response.message || "Template deleted successfully");
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete template");
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.template.trim()) {
      toast.error("Name and template content are required");
      return;
    }

    try {
      const templateData = {
        tempId: editingTemplate?.id || null,
        name: formData.name,
        tempContent: formData.template,
      };
      const response = await settingsAPI.storeFollowUpTemplate(templateData);
      if (response.success) {
        toast.success(
          editingTemplate
            ? "Template updated successfully"
            : "Template created successfully"
        );
        setShowModal(false);
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message || "Failed to save template");
    }
  };

  const sortedFilteredTemplates = useMemo(() => {
    const list = [...filteredTemplates];
    const mul = sortConfig.direction === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortConfig.key) {
        case "id":
          return mul * ((Number(a.id) || 0) - (Number(b.id) || 0));
        case "name":
          return mul * String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
        case "template":
          return mul * String(a.template || "").localeCompare(String(b.template || ""), undefined, { sensitivity: "base" });
        default:
          return 0;
      }
    });
    return list;
  }, [filteredTemplates, sortConfig]);

  const effectiveItemsPerPage =
    itemsPerPage === "all" ? sortedFilteredTemplates.length || 1 : (Number(itemsPerPage) || 30);
  const paginatedTemplates =
    itemsPerPage === "all"
      ? sortedFilteredTemplates
      : sortedFilteredTemplates.slice(
        (currentPage - 1) * effectiveItemsPerPage,
        currentPage * effectiveItemsPerPage
      );
  const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(sortedFilteredTemplates.length / effectiveItemsPerPage);
  const entriesValue = String(itemsPerPage);

  /** When sorting by # (id) descending, show 3,2,1…; otherwise show 1,2,3… in current sort order. */
  const rowNumberForTemplateIndex = (rowIndex) => {
    const total = sortedFilteredTemplates.length;
    if (total === 0) return rowIndex + 1;
    const pos = (currentPage - 1) * effectiveItemsPerPage + rowIndex;
    if (sortConfig.key === "id" && sortConfig.direction === "desc") {
      return total - pos;
    }
    return pos + 1;
  };

  const columns = [
    {
      key: "rowNum",
      sortKey: "id",
      title: "#",
      sortable: true,
      className: "reusable-table-centered-col",
      render: (_row, rowIndex) => rowNumberForTemplateIndex(rowIndex),
    },
    { key: "name", title: "Template Name", sortable: true, render: (row) => row.name || "N/A" },
    {
      key: "template",
      title: "Content",
      sortable: true,
      className: "follow-up-col-template",
      render: (row) => {
        const content = row.template || "";
        if (!content) return "N/A";
        return <div className="follow-up-template-content-full">{content}</div>;
      },
    },
    {
      key: "action",
      title: "Action",
      className: "reusable-table-centered-col",
      render: (row) => (
        <ActionButtons
          onEdit={() => handleEditTemplate(row.id)}
          onDelete={() => handleDeleteTemplate(row.id)}
          hideView
        />
      ),
    },
  ];

  return (
    <div className="follow-up-setting">
      <div className="follow-up-top-bar list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Follow Up Template" level="h3" className="setting-subsection-title list-screen-title-text" />
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            inputId="follow-up-template-search"
          />
          <button className="list-screen-add-btn" onClick={handleAddTemplate}>
            <FontAwesomeIcon icon={faPlus} /> Add Template
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonLoader type="mediaRows" count={2} />
      ) : (
        <>
          <div className="follow-up-table-container">
            <Table
              columns={columns}
              data={paginatedTemplates}
              sortBy={sortConfig.key}
              sortOrder={sortConfig.direction}
              onSort={requestSort}
              isLoading={false}
              loadingMode="skeleton"
              skeletonRows={Math.min(effectiveItemsPerPage, 10)}
              emptyMessage="No templates found"
            />
          </div>

          <ListScreenPaginationBar
            entriesValue={entriesValue}
            onEntriesChange={(value) => {
              setItemsPerPage(value === "all" ? "all" : Number(value));
              setCurrentPage(1);
            }}
            currentPage={currentPage}
            totalPages={Math.max(totalPages || 1, 1)}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </>
      )}

      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {editingTemplate ? "Edit Template" : "Add Template"}
            </h3>
            <form onSubmit={handleSaveTemplate}>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ display: "block", marginBottom: "5px" }}>
                  Template Content *
                </label>
                <textarea
                  value={formData.template}
                  onChange={(e) =>
                    setFormData({ ...formData, template: e.target.value })
                  }
                  required
                  rows="10"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: "8px 16px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: "white",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: "8px 16px",
                    border: "none",
                    borderRadius: "4px",
                    background: "#337ab7",
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Calendar Permission Component ---
const CalendarPermissionSetting = () => {
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCaregivers, setSelectedCaregivers] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({
    create: false,
    view: false,
    edit: false,
    delete: false,
  });
  const [editingGroupKey, setEditingGroupKey] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [caregiverSearch, setCaregiverSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteGroupKey, setDeleteGroupKey] = useState(null);
  const [deleteCaregiverName, setDeleteCaregiverName] = useState("");
  const [isDeletingPermission, setIsDeletingPermission] = useState(false);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const [permsRes, caregiversRes] = await Promise.all([
        settingsAPI.getCalendarPermissions(),
        settingsAPI.getCaregiversForPermission(),
      ]);

      if (permsRes.success) {
        setPermissions(permsRes.data || []);
        // Group permissions by caregiver_id and practice_id
        const grouped = {};
        permsRes.data.forEach((perm) => {
          const key =
            perm.practice_id !== null
              ? `${perm.caregiver_id}-${perm.practice_id}`
              : `${perm.caregiver_id}`;
          if (!grouped[key]) {
            grouped[key] = {
              caregiver_id: perm.caregiver_id,
              practice_id: perm.practice_id,
              caregiver_name: perm.caregiver_name,
              practice_name: perm.practice_name,
              created_by_name: perm.created_by_name,
              permissions: [],
            };
          }
          grouped[key].permissions.push(perm.permission_name);
        });
        setGroupedPermissions(grouped);
      }

      if (caregiversRes.success) {
        setCaregivers(caregiversRes.data || []);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch data");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        !event.target.closest(".caregiver-dropdown-wrapper")
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSavePermissions = async () => {
    if (isSaving) return;

    if (selectedCaregivers.length === 0) {
      toast.error("Please select at least one caregiver");
      return;
    }

    const permNames = Object.keys(selectedPermissions).filter(
      (key) => selectedPermissions[key]
    );
    if (permNames.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    try {
      setIsSaving(true);
      const response = await settingsAPI.createCalendarPermission({
        caregivers: selectedCaregivers,
        permissions: permNames,
        groupKey: editingGroupKey,
      });

      if (response.success) {
        toast.success(response.message || "Permissions saved successfully!");
        setSelectedCaregivers([]);
        setSelectedPermissions({ create: false, view: false, edit: false, delete: false });
        setEditingGroupKey(null);
        fetchData({ silent: true });
      }
    } catch (error) {
      toast.error(error.message || "Failed to save permissions");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditPermissions = (groupKey) => {
    const group = groupedPermissions[groupKey];
    if (group) {
      const normalizedPermissions = (group.permissions || []).map((p) =>
        String(p || "").toLowerCase()
      );
      setSelectedCaregivers([String(group.caregiver_id)]);
      setEditingGroupKey(groupKey);
      setSelectedPermissions({
        create: normalizedPermissions.includes("create"),
        view: normalizedPermissions.includes("view"),
        edit: normalizedPermissions.includes("edit"),
        delete: normalizedPermissions.includes("delete"),
      });
      setIsDropdownOpen(false);
      setCaregiverSearch("");
    }
  };

  const openDeletePermissionModal = (groupKey, caregiverName = "") => {
    setDeleteGroupKey(groupKey);
    setDeleteCaregiverName(caregiverName || "this system caregiver");
    setShowDeleteModal(true);
  };

  const closeDeletePermissionModal = (force = false) => {
    if (isDeletingPermission && !force) return;
    setShowDeleteModal(false);
    setDeleteGroupKey(null);
    setDeleteCaregiverName("");
  };

  const handleDeletePermissions = async () => {
    if (!deleteGroupKey) return;
    try {
      setIsDeletingPermission(true);
      const response = await settingsAPI.deleteCalendarPermission(deleteGroupKey);
      if (response.success) {
        toast.success(response.message || "Permission deleted successfully");
        closeDeletePermissionModal(true);
        fetchData({ silent: true });
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete permissions");
    } finally {
      setIsDeletingPermission(false);
    }
  };

  const filteredGroups = Object.keys(groupedPermissions).filter((key) => {
    if (!searchTerm.trim()) return true;
    const group = groupedPermissions[key];
    const searchLower = searchTerm.toLowerCase();
    return (
      group.caregiver_name?.toLowerCase().includes(searchLower) ||
      group.practice_name?.toLowerCase().includes(searchLower)
    );
  });

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredCaregivers = caregivers.filter((cg) => {
    if (!caregiverSearch.trim()) return true;
    const term = caregiverSearch.toLowerCase();
    const practice = cg.practice_name ? ` ${cg.practice_name}` : "";
    return `${cg.name}${practice}`.toLowerCase().includes(term);
  });

  const tableData = filteredGroups.map((key) => ({
    _groupKey: key,
    ...groupedPermissions[key],
  }));

  const PERMISSION_ORDER = ["Create", "View", "Edit", "Delete"];
  const sortPermissionsByOrder = (permissions = []) => {
    const unique = Array.from(new Set(permissions));
    return unique.sort((a, b) => {
      const idxA = PERMISSION_ORDER.indexOf(a);
      const idxB = PERMISSION_ORDER.indexOf(b);
      const safeA = idxA === -1 ? Number.MAX_SAFE_INTEGER : idxA;
      const safeB = idxB === -1 ? Number.MAX_SAFE_INTEGER : idxB;
      return safeA - safeB;
    });
  };

  const sortedTableData = useMemo(() => {
    if (!sortConfig.key) {
      return tableData;
    }
    const list = [...tableData];
    const mul = sortConfig.direction === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortConfig.key) {
        case "rowNo":
          return mul * ((Number(a.caregiver_id) || 0) - (Number(b.caregiver_id) || 0));
        case "caregiver_name":
          return mul * String(a.caregiver_name || "").localeCompare(String(b.caregiver_name || ""), undefined, { sensitivity: "base" });
        case "permissions":
          return mul * sortPermissionsByOrder(a.permissions || []).join(",").localeCompare(
            sortPermissionsByOrder(b.permissions || []).join(","),
            undefined,
            { sensitivity: "base" }
          );
        case "created_by_name":
          return mul * String(a.created_by_name || "").localeCompare(String(b.created_by_name || ""), undefined, { sensitivity: "base" });
        default:
          return 0;
      }
    });
    return list;
  }, [tableData, sortConfig]);

  const columns = [
    {
      key: "rowNo",
      title: "#",
      sortable: true,
      sortKey: "rowNo",
      className: "reusable-table-centered-col",
      render: (_row, rowIndex) => rowIndex + 1,
    },
    { key: "caregiver_name", title: "System Caregiver", sortable: true, render: (row) => row.caregiver_name || "N/A" },
    {
      key: "permissions",
      title: "Permissions",
      sortable: true,
      render: (row) =>
        sortPermissionsByOrder(row.permissions || []).map((perm) => (
          <span key={perm} className={`permission-badge ${perm.toLowerCase()}`}>
            {perm}
          </span>
        )),
    },
    { key: "created_by_name", title: "Created By", sortable: true, render: (row) => row.created_by_name || "N/A" },
    {
      key: "actions",
      title: "Actions",
      className: "reusable-table-centered-col",
      render: (row) => (
        <ActionButtons
          onEdit={() => handleEditPermissions(row._groupKey)}
          onDelete={() => openDeletePermissionModal(row._groupKey, row.caregiver_name)}
          isDeleting={isDeletingPermission && deleteGroupKey === row._groupKey}
          hideView
        />
      ),
    },
  ];

  return (
    <div className="calendar-permission-setting">
      <div className="calendar-permission-top-bar list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Calendar Permission Settings" level="h3" className="setting-subsection-title list-screen-title-text" />
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            inputId="calendar-permission-search"
          />
        </div>
      </div>

      <div className="add-caregiver-form">
        <div className="caregiver-controls-row">
          <div className="caregiver-info-group">
            <label htmlFor="caregiver-select">
              System Caregiver <span className="required">*</span>
            </label>
            <div className="caregiver-dropdown-wrapper">
              <div
                className="caregiver-select-box"
                onClick={() => setIsDropdownOpen(true)}
              >
                <input
                  type="text"
                  className="caregiver-select-input"
                  value={isDropdownOpen ? caregiverSearch : (
                    selectedCaregivers.length === 0
                      ? ""
                      : selectedCaregivers.length === 1
                        ? (caregivers.find((cg) => String(cg.id) === String(selectedCaregivers[0]))?.name || "Selected")
                        : `${selectedCaregivers.length} caregivers selected`
                  )}
                  onChange={(e) => {
                    setCaregiverSearch(e.target.value);
                    if (!isDropdownOpen) setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  placeholder="Click or type to search caregivers..."
                />
                <span className="caregiver-dropdown-arrow">▼</span>
              </div>
              {isDropdownOpen && (
                <div className="caregiver-dropdown-list">
                  {filteredCaregivers.length === 0 ? (
                    <div className="caregiver-dropdown-item disabled">
                      No caregivers found
                    </div>
                  ) : (
                    filteredCaregivers.map((cg) => {
                      const isSelected = selectedCaregivers.map(String).includes(String(cg.id));
                      return (
                        <div
                          key={cg.id}
                          className={`caregiver-dropdown-item ${isSelected ? "selected" : ""}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedCaregivers(
                                selectedCaregivers.filter((id) => String(id) !== String(cg.id))
                              );
                            } else {
                              setSelectedCaregivers([...selectedCaregivers, String(cg.id)]);
                            }
                            setCaregiverSearch("");
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span>
                            {cg.name} {cg.practice_name ? `(${cg.practice_name})` : ""}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="permission-checkboxes">
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.create}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    create: e.target.checked,
                  })
                }
              />{" "}
              Create
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.view}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    view: e.target.checked,
                  })
                }
              />{" "}
              View
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.edit}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    edit: e.target.checked,
                  })
                }
              />{" "}
              Edit
            </label>
            <label>
              <input
                type="checkbox"
                checked={selectedPermissions.delete}
                onChange={(e) =>
                  setSelectedPermissions({
                    ...selectedPermissions,
                    delete: e.target.checked,
                  })
                }
              />{" "}
              Delete
            </label>
          </div>

          <div className="save-btn-wrapper">
            <button className="list-screen-add-btn" onClick={handleSavePermissions} disabled={isSaving}>
              <FontAwesomeIcon icon={faCheck} /> {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>



      {loading ? (
        <SkeletonLoader type="mediaRows" count={2} />
      ) : (
        <div className="calendar-table-container">
          <Table
            columns={columns}
            data={sortedTableData}
            sortBy={sortConfig.key}
            sortOrder={sortConfig.direction}
            onSort={requestSort}
            isLoading={false}
            loadingMode="skeleton"
            skeletonRows={8}
            emptyMessage="No permissions found"
          />
        </div>
      )}

      {showDeleteModal && (
        <div className="practices-list-modal-overlay">
          <div className="practices-list-modal-container">
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={closeDeletePermissionModal}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body">
              <p>
                Are you sure you want to delete calendar permissions for{" "}
                <strong>{deleteCaregiverName}</strong>?
              </p>
              <p className="practices-list-modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="practices-list-modal-footer">
              <button
                type="button"
                className="practices-list-btn-cancel"
                onClick={closeDeletePermissionModal}
                disabled={isDeletingPermission}
              >
                Cancel
              </button>
              <button
                type="button"
                className="practices-list-btn-delete"
                onClick={handleDeletePermissions}
                disabled={isDeletingPermission}
              >
                {isDeletingPermission ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="list-screen-me-2" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Settings Component ---
const Settings = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth || {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const isSystemCaregiver = user?.role_id === 7;
  const isPracticeCaregiver = user?.role_id === 5;
  const hideCalendarPermissionTab = isSystemCaregiver || isPracticeCaregiver;
  const cachedAccountSnapshot = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const user = parsed?.user || parsed;
      if (!user) return null;
      return {
        full_name: `${user.last_name || ""} ${user.first_name || ""}`.trim() || "-",
        phone: user.phone || "-",
        username: user.username || "-",
        fax: user.fax || "-",
        email: user.email || "-",
        session_time: user.session_time || 30,
        is_two_way_auth: user.is_two_way_auth || 0
      };
    } catch (_) {
      return null;
    }
  })();
  const [activeTab, setActiveTab] = useState("account");
  const [accountData, setAccountData] = useState(cachedAccountSnapshot);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [sessionTime, setSessionTime] = useState(cachedAccountSnapshot?.session_time || 60);
  const [twoWayAuthEnabled, setTwoWayAuthEnabled] = useState(cachedAccountSnapshot?.is_two_way_auth === 1);

  useEffect(() => {
    if (hideCalendarPermissionTab && activeTab === "calendar") {
      setActiveTab("account");
    }
  }, [hideCalendarPermissionTab, activeTab]);

  useEffect(() => {
    const tabLabelMap = {
      account: "Account Setting",
      followup: "Follow Up Setting",
      ...(!hideCalendarPermissionTab ? { calendar: "Calendar Permission Settings" } : {})
    };
    updateBreadcrumbs([
      { label: "Settings", path: "/settings" },
      { label: tabLabelMap[activeTab] || "Settings", path: null, active: true }
    ]);
    return () => {
      clearBreadcrumbs();
    };
  }, [activeTab, updateBreadcrumbs, clearBreadcrumbs, hideCalendarPermissionTab]);

  useEffect(() => {
    if (activeTab !== "account" || accountLoaded) return;
    fetchAccountSettings();
  }, [activeTab, accountLoaded]);

  const fetchAccountSettings = async () => {
    try {
      const response = await settingsAPI.getUserAccountSettings();
      if (response.success) {
        setAccountData(response.data);
        setSessionTime(response.data.session_time || 60);
        setTwoWayAuthEnabled(response.data.is_two_way_auth === 1);
        setAccountLoaded(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch account settings");
    }
  };

  // --- Main Settings Component ---
  const handleUpdateSession = async () => {
    try {
      const response = await settingsAPI.updateSessionSettings(sessionTime);
      if (response.success) {
        toast.success(response.message || "Session time updated successfully");
        dispatch(updateUserSessionTime(sessionTime));
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to update session time");
    }
  };

  const toggleTwoWayAuth = async () => {
    const newStatus = twoWayAuthEnabled ? 0 : 1;
    try {
      const response = await settingsAPI.toggleTwoWayAuth(newStatus);
      if (response.success) {
        toast.success(response.message || "Two-way authentication updated");
        setTwoWayAuthEnabled(newStatus === 1);
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || "Failed to toggle two-way authentication");
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await settingsAPI.clearCache();
      if (response.success) {
        toast.success(response.message || "Cache cleared successfully");
      }
    } catch (error) {
      toast.error(error.message || "Failed to clear cache");
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "account":
        if (!accountData) {
          return <div style={{ textAlign: "center", padding: "40px" }}>No data available</div>;
        }
        return (
          <div className="saas-settings-container">
            {/* Profile Information Section */}
            <div className="saas-settings-card">
              <div className="saas-card-header">
                <div className="saas-card-icon-title">
                  <FontAwesomeIcon icon={faUser} className="saas-card-icon" />
                  <div>
                    <h4 className="saas-card-title">Profile Information</h4>
                    <p className="saas-card-subtitle">Your personal account details and contact information.</p>
                  </div>
                </div>
              </div>
              <div className="saas-card-body">
                <div className="saas-info-grid">
                  <div className="saas-field-tile">
                    <span className="saas-field-label">User Name</span>
                    <div className="saas-field-value">{accountData.full_name || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Username</span>
                    <div className="saas-field-value">{accountData.username || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Email Address</span>
                    <div className="saas-field-value">{accountData.email || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Phone Number</span>
                    <div className="saas-field-value">{accountData.phone || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Fax</span>
                    <div className="saas-field-value">{accountData.fax || "-"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security & Authentication Section */}
            <div className="saas-settings-card">
              <div className="saas-card-header">
                <div className="saas-card-icon-title">
                  <FontAwesomeIcon icon={faShieldAlt} className="saas-card-icon" />
                  <div>
                    <h4 className="saas-card-title">Security & Authentication</h4>
                    <p className="saas-card-subtitle">Manage two-factor authentication and login verification.</p>
                  </div>
                </div>
              </div>
              <div className="saas-card-body">
                <div className="saas-setting-row">
                  <div className="saas-row-info">
                    <h5 className="saas-row-title">Two-Way Authentication</h5>
                    <p className="saas-row-description">Require verification for additional account access security.</p>
                  </div>
                  <div className="saas-row-action">
                    <button
                      type="button"
                      className={`saas-toggle-badge ${twoWayAuthEnabled ? "enabled" : "disabled"}`}
                      onClick={toggleTwoWayAuth}
                    >
                      <FontAwesomeIcon icon={faLock} className="me-1" />
                      {twoWayAuthEnabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Session & Preferences Section */}
            <div className="saas-settings-card">
              <div className="saas-card-header">
                <div className="saas-card-icon-title">
                  <FontAwesomeIcon icon={faClock} className="saas-card-icon" />
                  <div>
                    <h4 className="saas-card-title">Session & Preferences</h4>
                    <p className="saas-card-subtitle">Configure session timeout limits and local cache preferences.</p>
                  </div>
                </div>
              </div>
              <div className="saas-card-body">
                <div className="saas-setting-row">
                  <div className="saas-row-info">
                    <h5 className="saas-row-title">Session Timeout Duration</h5>
                    <p className="saas-row-description">Set inactivity timeout limit in minutes (Current: <strong>{accountData.session_time || 60} mins</strong>).</p>
                  </div>
                  <div className="saas-row-action saas-session-action">
                    <input
                      type="number"
                      className="form-input-custom saas-session-input"
                      min={1}
                      value={sessionTime || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSessionTime(val === "" ? "" : parseInt(val));
                      }}
                      placeholder="Minutes"
                    />
                    <button
                      type="button"
                      className="list-screen-add-btn"
                      onClick={() => {
                        const timeToUpdate = Number(sessionTime);
                        if (!timeToUpdate || timeToUpdate < 1) {
                          toast.error("Please enter a valid session time (min 1 minute)");
                          return;
                        }
                        handleUpdateSession();
                      }}
                    >
                      Update
                    </button>
                  </div>
                </div>

                <div className="saas-setting-row border-top">
                  <div className="saas-row-info">
                    <h5 className="saas-row-title">System Cache</h5>
                    <p className="saas-row-description">Clear cached application data to reload updated permissions and data state.</p>
                  </div>
                  <div className="saas-row-action">
                    <button type="button" className="btn-secondary" onClick={handleClearCache}>
                      <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Refresh Cache
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "followup":
        return <FollowUpSetting />;
      // System Caregiver Calendar Permission disabled
      // case "calendar":
      //   return hideCalendarPermissionTab ? null : <CalendarPermissionSetting />;
      default:
        return null;
    }
  };

  return (
    <div className="settings-page-wrapper">
      <Heading title="Settings" level="h2" className="settings-page-title list-screen-title-text" />
      <div className="settings-card">
        <div className="settings-tabs" role="tablist">
          <button
            className={`tab ${activeTab === "account" ? "active" : ""}`}
            onClick={() => setActiveTab("account")}
          >
            Account Setting
          </button>
          <button
            className={`tab ${activeTab === "followup" ? "active" : ""}`}
            onClick={() => setActiveTab("followup")}
          >
            Follow Up Setting
          </button>
          {/* System Caregiver Calendar Permission tab disabled
          {!hideCalendarPermissionTab && (
            <button
              className={`tab ${activeTab === "calendar" ? "active" : ""}`}
              onClick={() => setActiveTab("calendar")}
            >
              Calendar Permission Settings
            </button>
          )}
          */}
        </div>
        <div className="settings-content">{renderContent()}</div>
      </div>
    </div>
  );
};

export default Settings;
